import type {
  Dashboard,
  DashboardWidget,
  PredictiveModel,
  BiExportJob,
  ReportDefinition,
} from './types';

export function adaptDashboard(raw: any): Dashboard {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'dash-fallback',
      tenant_id: 'default',
      title: 'Executive Overview',
      description: 'Key performance indicators and operational metrics',
      layout_type: 'grid',
      widgets: [],
      refresh_interval_sec: 60,
      is_default: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'dash-fallback'),
    tenant_id: String(raw.tenant_id || 'default'),
    title: String(raw.title || 'Executive Overview'),
    description: String(raw.description || ''),
    layout_type: (raw.layout_type as any) || 'grid',
    widgets: Array.isArray(raw.widgets) ? raw.widgets.map(adaptWidget) : [],
    refresh_interval_sec: Number(raw.refresh_interval_sec) || 60,
    is_default: Boolean(raw.is_default),
    created_at: String(raw.created_at || new Date().toISOString()),
    updated_at: String(raw.updated_at || new Date().toISOString()),
  };
}

export function adaptWidget(raw: any): DashboardWidget {
  return {
    id: String(raw?.id || 'w-fallback'),
    title: String(raw?.title || 'Metric'),
    type: (raw?.type as any) || 'kpi_card',
    metric: String(raw?.metric || 'count'),
    dimensions: Array.isArray(raw?.dimensions) ? raw.dimensions.map(String) : [],
    aggregation: (raw?.aggregation as any) || 'sum',
    value: Number(raw?.value) || 0,
    delta_pct: Number(raw?.delta_pct) || 0,
    chart_data: Array.isArray(raw?.chart_data) ? raw.chart_data : undefined,
    grid_w: Number(raw?.grid_w) || 4,
    grid_h: Number(raw?.grid_h) || 2,
    grid_x: Number(raw?.grid_x) || 0,
    grid_y: Number(raw?.grid_y) || 0,
  };
}

export function adaptPredictiveModel(raw: any): PredictiveModel {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'model-fallback',
      tenant_id: 'default',
      name: 'Revenue Forecast',
      type: 'revenue_forecast',
      status: 'ready',
      confidence_pct: 92.4,
      target_horizon: 'Q4 2026',
      summary_text: 'Expected recurring revenue growth trajectory',
      predicted_value: '$1,250,000',
      key_drivers: ['Renewal velocity', 'Lead expansion'],
      data_points: [],
      last_run_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'model-fallback'),
    tenant_id: String(raw.tenant_id || 'default'),
    name: String(raw.name || 'Predictive Model'),
    type: (raw.type as any) || 'revenue_forecast',
    status: (raw.status as any) || 'ready',
    confidence_pct: Number(raw.confidence_pct) || 0,
    target_horizon: String(raw.target_horizon || 'Next 30 Days'),
    summary_text: String(raw.summary_text || ''),
    predicted_value: String(raw.predicted_value || 'N/A'),
    key_drivers: Array.isArray(raw.key_drivers) ? raw.key_drivers.map(String) : [],
    data_points: Array.isArray(raw.data_points) ? raw.data_points : [],
    last_run_at: String(raw.last_run_at || new Date().toISOString()),
  };
}

export function adaptBiExportJob(raw: any): BiExportJob {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'job-fallback',
      tenant_id: 'default',
      name: 'Snowflake Analytics Warehouse',
      target_destination: 'snowflake',
      sync_frequency: 'daily',
      status: 'synced',
      records_synced: 14200,
      destination_schema: 'helios_raw',
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'job-fallback'),
    tenant_id: String(raw.tenant_id || 'default'),
    name: String(raw.name || 'BI Export Job'),
    target_destination: (raw.target_destination as any) || 'snowflake',
    sync_frequency: (raw.sync_frequency as any) || 'daily',
    status: (raw.status as any) || 'active',
    records_synced: Number(raw.records_synced) || 0,
    destination_schema: String(raw.destination_schema || 'public'),
    last_sync_at: raw.last_sync_at ? String(raw.last_sync_at) : undefined,
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export function adaptReportDefinition(raw: any): ReportDefinition {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'rep-fallback',
      tenant_id: 'default',
      title: 'Quarterly Sales Pipeline',
      category: 'Sales',
      source_entity: 'deal',
      columns: ['title', 'stage', 'amount_minor'],
      formulas: [],
      schedule: 'weekly',
      output_format: 'csv',
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'rep-fallback'),
    tenant_id: String(raw.tenant_id || 'default'),
    title: String(raw.title || 'Untitled Report'),
    category: String(raw.category || 'General'),
    source_entity: String(raw.source_entity || 'contact'),
    columns: Array.isArray(raw.columns) ? raw.columns.map(String) : [],
    formulas: Array.isArray(raw.formulas) ? raw.formulas.map(String) : [],
    schedule: (raw.schedule as any) || 'on_demand',
    output_format: (raw.output_format as any) || 'csv',
    last_generated_at: raw.last_generated_at ? String(raw.last_generated_at) : undefined,
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export const sampleDashboards: Dashboard[] = [
  {
    id: '1',
    tenant_id: 'default',
    title: 'Executive Command Center',
    description: 'Real-time operational health, pipeline velocity, and active users',
    layout_type: 'grid',
    widgets: [
      { id: 'w1', title: 'Monthly Recurring Revenue', type: 'kpi_card', metric: 'mrr', dimensions: [], aggregation: 'sum', value: 245000, delta_pct: 12.4, grid_w: 4, grid_h: 2, grid_x: 0, grid_y: 0 },
      { id: 'w2', title: 'Active Tenant Users', type: 'kpi_card', metric: 'active_users', dimensions: [], aggregation: 'count', value: 842, delta_pct: 5.1, grid_w: 4, grid_h: 2, grid_x: 4, grid_y: 0 },
    ],
    refresh_interval_sec: 60,
    is_default: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

export const samplePredictiveModels: PredictiveModel[] = [
  {
    id: '1',
    tenant_id: 'default',
    name: 'Q4 Deal Win Probability',
    type: 'revenue_forecast',
    status: 'ready',
    confidence_pct: 88.5,
    target_horizon: '90 Days',
    summary_text: 'Expected closing velocity increases by 14% based on historical pattern clusters.',
    predicted_value: '$1,840,000',
    key_drivers: ['Stage dwell time < 10 days', 'C-Level contact engaged'],
    data_points: [{ label: 'Oct', value: 450000 }, { label: 'Nov', value: 680000 }, { label: 'Dec', value: 710000 }],
    last_run_at: new Date().toISOString(),
  },
];
